import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MDSChip, MDSChipVariant } from "@/mds/components/chips";
import {
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownPopover,
  MDSMenuItemIconVariant,
  MDSMenuItemSelectionVariant,
} from "@/mds/components/menu";
import { MDSTextFieldSlotName, MDSTextFieldVariant } from "@/mds/components/forms/text-field";
import { StyledContainer, StyledTextField, StyledSelectMenu } from "./chip-input.style";
import { MDSChipInputItem, MDSChipInputProps } from "./chip-input.types";

export const MDSChipInput: FC<MDSChipInputProps> = ({
  isDisabled,
  getChips,
  chips,
  setChips,
  onSubmit,
  placeholder,
  textFieldVariant,
  autoFocus,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const availableChipElementsRef = useRef(new Map<string, HTMLDivElement>());
  const availableChipRefCallbacksRef = useRef(
    new Map<string, (element: HTMLDivElement | null) => void>()
  );

  const [value, setValue] = useState<string>("");
  const [availableChips, setAvailableChips] = useState<MDSChipInputItem[]>([]);
  const [highlightedChipId, setHighlightedChipId] = useState<string | undefined>();

  useEffect(() => {
    const fetchAvailableChips = async (value: string) => {
      const chips = await getChips(value);
      setAvailableChips(chips);
    };
    fetchAvailableChips(value);
  }, [getChips, value]);

  const sortedAvailableChips = useMemo(
    () => [...availableChips].sort((a, b) => b.orderByString.localeCompare(a.orderByString)),
    [availableChips]
  );
  const highlightedChip =
    sortedAvailableChips.find(chip => chip.id === highlightedChipId) ?? sortedAvailableChips[0];

  useEffect(() => {
    setHighlightedChipId(sortedAvailableChips[0]?.id);
  }, [sortedAvailableChips]);

  useEffect(() => {
    const availableChipIds = new Set(sortedAvailableChips.map(chip => chip.id));

    for (const itemId of availableChipElementsRef.current.keys()) {
      if (!availableChipIds.has(itemId)) {
        availableChipElementsRef.current.delete(itemId);
      }
    }

    for (const itemId of availableChipRefCallbacksRef.current.keys()) {
      if (!availableChipIds.has(itemId)) {
        availableChipRefCallbacksRef.current.delete(itemId);
      }
    }
  }, [sortedAvailableChips]);

  const getAvailableChipElementRef = useCallback(({ itemId }: { itemId: string }) => {
    const existingRefCallback = availableChipRefCallbacksRef.current.get(itemId);
    if (existingRefCallback) return existingRefCallback;

    const refCallback = (element: HTMLDivElement | null) => {
      if (!element) {
        availableChipElementsRef.current.delete(itemId);
        return;
      }

      availableChipElementsRef.current.set(itemId, element);
    };

    availableChipRefCallbacksRef.current.set(itemId, refCallback);
    return refCallback;
  }, []);

  useLayoutEffect(() => {
    if (!highlightedChipId) return;

    availableChipElementsRef.current.get(highlightedChipId)?.scrollIntoView?.({ block: "nearest" });
  }, [highlightedChipId]);

  const contentList: MDSMenuContentList = sortedAvailableChips.map(chip => ({
    id: chip.id,
    kind: MDSMenuContentItemKind.Item,
    iconVariant: MDSMenuItemIconVariant.Avatar,
    icon: chip.icon ? props => chip.icon?.(props) : undefined,
    label: chip.label,
    subtitle: chip.secondaryLabel,
    isSelected: chip.id === highlightedChip?.id,
    selectionVariant: MDSMenuItemSelectionVariant.Hover,
    onHover: () => setHighlightedChipId(chip.id),
    onClick: () => {
      handleSelectChip(chip);
      setValue("");
    },
  }));

  const handleSelectChip = (chip: MDSChipInputItem) => {
    setChips(chips => {
      const hasChip = chips.some(c => c.id === chip.id);
      if (hasChip) return chips;
      return [...chips, chip];
    });
    inputRef.current?.focus();
  };

  const handleRemoveChip = (chip: MDSChipInputItem) => {
    setChips(chips => chips.filter(c => c.id !== chip.id));
  };

  const handleInputChange = (value: string) => {
    if (value.endsWith(",") || value.endsWith(" ")) {
      const trimmedValue = value.substring(0, value.length - 1).trim();
      const matchingChips = availableChips.filter(chip =>
        chip.searchLabels?.includes(trimmedValue)
      );
      if (matchingChips.length === 1) {
        handleSelectChip(matchingChips[0]);
        setValue("");
        return;
      }
    }
    setValue(value);
  };

  const moveHighlightedChip = ({ offset }: { offset: 1 | -1 }) => {
    if (sortedAvailableChips.length === 0) return;

    const currentIndex = highlightedChip
      ? sortedAvailableChips.findIndex(chip => chip.id === highlightedChip.id)
      : -1;
    const nextIndex =
      currentIndex === -1
        ? 0
        : (currentIndex + offset + sortedAvailableChips.length) % sortedAvailableChips.length;

    setHighlightedChipId(sortedAvailableChips[nextIndex].id);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "Enter": {
        if (highlightedChip && (value.length > 0 || chips.length === 0)) {
          e.preventDefault();
          handleSelectChip(highlightedChip);
          setValue("");
          return;
        }

        if (value.length === 0 && chips.length > 0) {
          e.preventDefault();
          onSubmit(chips);
          return;
        }
        break;
      }
      case "ArrowUp": {
        if (sortedAvailableChips.length === 0) return;

        e.preventDefault();
        moveHighlightedChip({ offset: -1 });
        return;
      }
      case "ArrowDown": {
        if (sortedAvailableChips.length === 0) return;

        e.preventDefault();
        moveHighlightedChip({ offset: 1 });
        return;
      }
      case "Backspace": {
        if (value.length === 0 && chips.length > 0) {
          e.preventDefault();
          handleRemoveChip(chips[chips.length - 1]);
          return;
        }
        break;
      }
    }
  };

  const stopEventPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <StyledContainer ref={triggerRef}>
      <StyledTextField
        variant={textFieldVariant ?? MDSTextFieldVariant.Default}
        placeholder={chips.length ? undefined : placeholder}
        onKeyDown={handleKeydown}
        value={value}
        onChange={handleInputChange}
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        ref={inputRef}
      >
        {chips.map(chip => (
          <MDSChip
            key={chip.id}
            variant={MDSChipVariant.Medium}
            slot={MDSTextFieldSlotName.LeftAddon}
            icon={chip.icon ? props => chip.icon?.(props) : undefined}
            onRemove={() => handleRemoveChip(chip)}
            isDisabled={isDisabled}
          >
            {chip.label}
          </MDSChip>
        ))}
      </StyledTextField>
      <div>
        <MDSMenuDropdownPopover
          triggerRef={triggerRef}
          isOpen={contentList.length > 0}
          placement="bottom start"
          isNonModal
          onPointerDown={stopEventPropagation}
          onMouseDown={stopEventPropagation}
          onClick={stopEventPropagation}
        >
          <StyledSelectMenu
            selectionBehavior="toggle"
            selectionMode="single"
            shouldFocusWrap={true}
            contentList={contentList}
            getItemRef={getAvailableChipElementRef}
          />
        </MDSMenuDropdownPopover>
      </div>
    </StyledContainer>
  );
};
